export const SetDataToLocalStorage = (key: string, value: any) => {
  const newvalue = typeof value === "string" ? value : JSON.stringify(value);
  return localStorage.setItem(key, newvalue);
};

export const GetDataFromLocalStorage = (key: string): string | null => {
  const result = localStorage.getItem(key);
  if (result) {
    return JSON.parse(result);
  }
  return null;
};

/**
 * Ramène une photo d'ordonnance au cadrage d'archivage avant tout contrôle de
 * taille.
 *
 * Sans ce passage, une photo prise au téléphone puis déposée depuis le poste de
 * l'officine partait brute : elle dépassait couramment le plafond unitaire, et
 * le web n'offre aucune porte de sortie — pas d'appareil photo intégré comme
 * sur mobile, l'utilisateur restait bloqué sans recours dans l'application.
 *
 * Le fichier d'origine est rendu tel quel dans trois cas : ce n'est pas une
 * image (un PDF ne se recompresse pas ainsi), elle tient déjà dans le cadre, ou
 * le navigateur ne sait pas la décoder. Un échec ici ne doit jamais empêcher un
 * dépôt : on laisse alors le contrôle de taille trancher.
 */
export const downscaleImage = async (
  file: File,
  maxEdge: number,
  quality: number,
): Promise<File> => {
  if (!file.type.startsWith("image/")) {
    return file;
  }

  try {
    // `imageOrientation: "from-image"` est indispensable : le canvas ignore
    // l'orientation EXIF, et sans lui une photo prise en portrait ressort
    // couchée une fois redessinée.
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const ratio = maxEdge / Math.max(bitmap.width, bitmap.height);

    if (ratio >= 1) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * ratio);
    canvas.height = Math.round(bitmap.height * ratio);

    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return file;
    }

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", quality);
    });

    if (!blob || blob.size >= file.size) {
      return file;
    }

    // Extension realignee sur le contenu : le canvas rend toujours du JPEG,
    // meme si la source etait un PNG. Le backend deduit le type du data-URI,
    // mais le nom reste affiche a l'utilisateur et sert de nom de piece jointe
    // dans le mail au preparatoire.
    const name = `${file.name.replace(/\.[^.]+$/, "")}.jpg`;
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
};
